import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FileText,
    Filter,
    RefreshCw,
    CheckCircle,
    AlertCircle,
    DollarSign,
    TrendingUp,
    Users,
    CreditCard,
    Activity,
    FileSpreadsheet,
    FileJson,
    File
} from 'lucide-react';
import { useAlert } from '../../../../contexts/AlertContext';
import GlassyDateTimePicker from '../../../../components/GlassyDateTimePicker';

const Container = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;

  @media (max-width: 640px) {
    gap: 1.25rem;
  }
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 24px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  backdrop-filter: blur(10px);

  @media (max-width: 640px) {
    padding: 1.1rem;
    gap: 1.1rem;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;

  .title-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    h3 {
      margin: 0;
      font-size: 1.25rem;
      color: #fff;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    p {
      margin: 0;
      font-size: 0.9rem;
      color: rgba(255, 255, 255, 0.5);
    }
  }

  .actions {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
  }

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;

    .actions {
      width: 100%;
      justify-content: flex-start;
    }
  }
`;

const QuickReportsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ReportCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 640px) {
    padding: 1.1rem;
    gap: 0.75rem;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-2px);
  }

  .icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    margin-bottom: 0.5rem;
  }

  h4 {
    margin: 0;
    font-size: 1.1rem;
    color: #fff;
    font-weight: 600;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.5);
    line-height: 1.5;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.8rem;
  border-radius: 99px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: auto;
  align-self: flex-start;
  
  &.generating {
    background: rgba(245, 158, 11, 0.15);
    color: #f59e0b;
    border: 1px solid rgba(245, 158, 11, 0.2);
  }
  
  &.ready {
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
    border: 1px solid rgba(16, 185, 129, 0.2);
  }
  
  &.error {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.2);
  }

  .spinner {
    width: 12px;
    height: 12px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.25rem;
  margin-top: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  label {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.6);
    font-weight: 500;
  }

  select, input {
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: #fff;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    font-size: 0.95rem;
    width: 100%;
    outline: none;
    transition: all 0.2s;

    &:focus {
      border-color: rgba(99, 102, 241, 0.5);
      background: rgba(0, 0, 0, 0.3);
    }

    option {
      background: #1a1d2d;
      color: #fff;
    }
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  min-width: 0;
  
  &.primary {
    background: linear-gradient(135deg, #4f46e5, #0ea5e9);
    color: white;
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
    
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(79, 70, 229, 0.4);
    }
  }
  
  &.secondary {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.08);
    
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.12);
    }
  }

  &.success {
    background: rgba(16, 185, 129, 0.1);
    color: #34d399;
    border: 1px solid rgba(16, 185, 129, 0.2);
    
    &:hover:not(:disabled) {
      background: rgba(16, 185, 129, 0.2);
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
  }
`;

const PreviewTable = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  overflow-x: auto;
  width: 100%;
  
  table {
    width: 100%;
    border-collapse: collapse;
    
    th {
      text-align: left;
      padding: 1rem 1.5rem;
      color: rgba(255, 255, 255, 0.4);
      font-size: 0.8rem;
      font-weight: 500;
      text-transform: uppercase;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      white-space: nowrap;
    }
    
    td {
      padding: 1rem 1.5rem;
      color: rgba(255, 255, 255, 0.8);
      font-size: 0.9rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      white-space: nowrap;
    }
    
    tr:last-child td {
      border-bottom: none;
    }
    
    tr:hover {
      background: rgba(255, 255, 255, 0.02);
    }

    @media (max-width: 640px) {
      min-width: 540px;

      th,
      td {
        padding: 0.75rem 1rem;
        white-space: normal;
        word-break: break-word;
      }
    }
  }
`;

const ReportsAndExports = () => {
    const { showAlert } = useAlert();
    const [activeFilters, setActiveFilters] = useState({
        dateRange: 'last-30-days',
        startDate: '',
        endDate: '',
        eventStatus: 'all',
        category: 'all'
    });

    const [reportStatus, setReportStatus] = useState({});
    const [previewData, setPreviewData] = useState(null);
    const [loading, setLoading] = useState(false);

    const quickReports = [
        {
            id: 'revenue-summary',
            icon: <DollarSign size={24} />,
            title: 'Revenue Summary',
            description: 'Comprehensive revenue breakdown by events and time periods',
            action: () => generateReport('revenue-summary')
        },
        {
            id: 'sales-performance',
            icon: <TrendingUp size={24} />,
            title: 'Sales Performance',
            description: 'Analysis of ticket sales, conversion rates, and trends',
            action: () => generateReport('sales-performance')
        },
        {
            id: 'event-analytics',
            icon: <Activity size={24} />,
            title: 'Event Analytics',
            description: 'Event-specific metrics including attendance and ratings',
            action: () => generateReport('event-analytics')
        },
        {
            id: 'user-activity',
            icon: <Users size={24} />,
            title: 'User Activity',
            description: 'Registration trends and user engagement behavior',
            action: () => generateReport('user-activity')
        },
        {
            id: 'financial-reconciliation',
            icon: <CreditCard size={24} />,
            title: 'Reconciliation',
            description: 'Payment processing, refunds, and transaction summaries',
            action: () => generateReport('financial-reconciliation')
        },
        {
            id: 'system-usage',
            icon: <Activity size={24} />,
            title: 'System Usage',
            description: 'Platform Statistics, performance metrics, and growth',
            action: () => generateReport('system-usage')
        }
    ];

    const handleFilterChange = (field, value) => {
        setActiveFilters(prev => ({ ...prev, [field]: value }));
    };

    const generateReport = async (reportType) => {
        try {
            setLoading(true);
            setReportStatus(prev => ({ ...prev, [reportType]: 'generating' }));

            // Simulate report generation
            await new Promise(resolve => setTimeout(resolve, 1500));

            // Generate mock preview data
            const mockData = generateMockData(reportType);
            setPreviewData(mockData);

            setReportStatus(prev => ({ ...prev, [reportType]: 'ready' }));
        } catch (error) {
            console.error('Error generating report:', error);
            setReportStatus(prev => ({ ...prev, [reportType]: 'error' }));
        } finally {
            setLoading(false);
        }
    };

    const generateMockData = (reportType) => {
        switch (reportType) {
            case 'revenue-summary':
                return {
                    type: 'revenue-summary',
                    title: 'Revenue Summary Report',
                    columns: ['Event', 'Category', 'Tickets Sold', 'Revenue', 'Date'],
                    rows: [
                        ['Summer Music Festival', 'Concert', '1,250', '$125,000', '2024-07-15'],
                        ['Tech Conference 2024', 'Conference', '445', '$89,000', '2024-08-22'],
                        ['Art Gallery Opening', 'Exhibition', '150', '$15,000', '2024-07-30'],
                        ['Comedy Night Live', 'Comedy', '320', '$32,000', '2024-08-05'],
                        ['Business Summit', 'Conference', '680', '$136,000', '2024-08-18']
                    ],
                    totalRecords: 156
                };

            case 'sales-performance':
                return {
                    type: 'sales-performance',
                    title: 'Sales Performance Report',
                    columns: ['Metric', 'Current Period', 'Previous Period', 'Change'],
                    rows: [
                        ['Total Sales', '$2,847,392', '$2,456,781', '+15.9%'],
                        ['Tickets Sold', '45,231', '39,876', '+13.4%'],
                        ['Conversion Rate', '3.8%', '3.2%', '+18.8%'],
                        ['Average Order Value', '$87.50', '$82.30', '+6.3%'],
                        ['Refund Rate', '2.1%', '2.8%', '-25.0%']
                    ],
                    totalRecords: 5
                };

            default:
                return {
                    type: reportType,
                    title: `${reportType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Report`,
                    columns: ['Date', 'Value', 'Status'],
                    rows: [
                        ['2024-07-15', '1,250', 'Active'],
                        ['2024-07-16', '1,180', 'Active'],
                        ['2024-07-17', '1,340', 'Active']
                    ],
                    totalRecords: 50
                };
        }
    };

    const exportReport = async (format) => {
        if (!previewData) return;

        try {
            setLoading(true);
            await new Promise(resolve => setTimeout(resolve, 1000));

            if (format === 'csv') {
                downloadCSV(previewData);
            } else if (format === 'json') {
                downloadJSON(previewData);
            } else if (format === 'pdf') {
                showAlert({
                    title: 'PDF Export',
                    message: 'PDF export initiated. The file will download shortly.'
                });
            }
        } catch (error) {
            console.error('Error exporting report:', error);
            showAlert({
                title: 'Export Failed',
                message: 'Failed to generate export. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    const downloadCSV = (data) => {
        const csvContent = [
            data.columns.join(','),
            ...data.rows.map(row => row.join(','))
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${data.type}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const downloadJSON = (data) => {
        const jsonContent = JSON.stringify({
            ...data,
            exportDate: new Date().toISOString(),
            filters: activeFilters
        }, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${data.type}-${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <Container
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
        >
            {/* Quick Reports Section */}
            <Section>
                <SectionHeader>
                    <div className="title-group">
                        <h3><FileText size={20} /> Quick Reports</h3>
                        <p>Generate common reports with pre-configured settings</p>
                    </div>
                </SectionHeader>
                <QuickReportsGrid>
                    {quickReports.map((report) => (
                        <ReportCard
                            key={report.id}
                            onClick={report.action}
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                        >
                            <div className="icon-wrapper">{report.icon}</div>
                            <h4>{report.title}</h4>
                            <p>{report.description}</p>

                            <AnimatePresence>
                                {reportStatus[report.id] && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        style={{ width: '100%' }}
                                    >
                                        <StatusBadge className={reportStatus[report.id]}>
                                            {reportStatus[report.id] === 'generating' && <div className="spinner" />}
                                            {reportStatus[report.id] === 'generating' && 'Generating...'}
                                            {reportStatus[report.id] === 'ready' && <><CheckCircle size={12} /> Ready</>}
                                            {reportStatus[report.id] === 'error' && <><AlertCircle size={12} /> Error</>}
                                        </StatusBadge>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </ReportCard>
                    ))}
                </QuickReportsGrid>
            </Section>

            {/* Custom Report Builder */}
            <Section>
                <SectionHeader>
                    <div className="title-group">
                        <h3><Filter size={20} /> Custom Report Builder</h3>
                        <p>Create customized reports with specific filters and data points</p>
                    </div>
                    <div className="actions">
                        <Button
                            className="secondary"
                            onClick={() => setActiveFilters({
                                dateRange: 'last-30-days',
                                startDate: '',
                                endDate: '',
                                eventStatus: 'all',
                                category: 'all'
                            })}
                        >
                            <RefreshCw size={16} /> Reset
                        </Button>
                        <Button
                            className="primary"
                            onClick={() => generateReport('custom')}
                            disabled={loading}
                        >
                            <FileText size={16} /> Generate
                        </Button>
                    </div>
                </SectionHeader>

                <FilterGrid>
                    <FormGroup>
                        <label>Date Range</label>
                        <select
                            value={activeFilters.dateRange}
                            onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                        >
                            <option value="last-7-days">Last 7 Days</option>
                            <option value="last-30-days">Last 30 Days</option>
                            <option value="last-90-days">Last 90 Days</option>
                            <option value="last-year">Last Year</option>
                            <option value="custom">Custom Date Range</option>
                        </select>
                    </FormGroup>

                    {activeFilters.dateRange === 'custom' && (
                        <>
                            <FormGroup>
                                <label>Start Date</label>
                                <GlassyDateTimePicker
                                    mode="date"
                                    value={activeFilters.startDate}
                                    onChange={(val) => handleFilterChange('startDate', val)}
                                    placeholder="Select start date"
                                />
                            </FormGroup>
                            <FormGroup>
                                <label>End Date</label>
                                <GlassyDateTimePicker
                                    mode="date"
                                    value={activeFilters.endDate}
                                    onChange={(val) => handleFilterChange('endDate', val)}
                                    placeholder="Select end date"
                                />
                            </FormGroup>
                        </>
                    )}

                    <FormGroup>
                        <label>Event Status</label>
                        <select
                            value={activeFilters.eventStatus}
                            onChange={(e) => handleFilterChange('eventStatus', e.target.value)}
                        >
                            <option value="all">All Events</option>
                            <option value="upcoming">Upcoming</option>
                            <option value="live">Live</option>
                            <option value="completed">Completed</option>
                        </select>
                    </FormGroup>

                    <FormGroup>
                        <label>Event Category</label>
                        <select
                            value={activeFilters.category}
                            onChange={(e) => handleFilterChange('category', e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            <option value="CONCERT">Concert</option>
                            <option value="THEATER">Theater</option>
                            <option value="SPORTS">Sports</option>
                            <option value="COMEDY">Comedy</option>
                            <option value="OTHER">Other</option>
                        </select>
                    </FormGroup>
                </FilterGrid>
            </Section>

            {/* Report Preview */}
            <AnimatePresence>
                {previewData && (
                    <Section as={motion.div} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                        <SectionHeader>
                            <div className="title-group">
                                <h3><FileText size={20} /> Report Preview</h3>
                                <p>Showing {previewData.rows.length} of {previewData.totalRecords} records</p>
                            </div>
                            <div className="actions">
                                <Button className="secondary" onClick={() => exportReport('csv')} disabled={loading}>
                                    <FileSpreadsheet size={16} /> CSV
                                </Button>
                                <Button className="secondary" onClick={() => exportReport('json')} disabled={loading}>
                                    <FileJson size={16} /> JSON
                                </Button>
                                <Button className="success" onClick={() => exportReport('pdf')} disabled={loading}>
                                    <File size={16} /> PDF
                                </Button>
                            </div>
                        </SectionHeader>

                        <div style={{ marginTop: '1rem' }}>
                            <h4 style={{ color: '#fff', marginBottom: '1rem' }}>{previewData.title}</h4>
                            <PreviewTable>
                                <table>
                                    <thead>
                                        <tr>
                                            {previewData.columns.map((col, idx) => (
                                                <th key={idx}>{col}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewData.rows.map((row, rIdx) => (
                                            <tr key={rIdx}>
                                                {row.map((cell, cIdx) => (
                                                    <td key={cIdx}>{cell}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </PreviewTable>
                        </div>
                    </Section>
                )}
            </AnimatePresence>
        </Container>
    );
};

export default ReportsAndExports; 
